import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Database, Upload, RefreshCw, Trash2, MapPin, 
  CheckCircle2, AlertCircle, ExternalLink, Info,
  Download, FileJson, Play, Zap, Loader2, Clock,
  Globe, ChevronDown, ChevronRight, Map, Building2, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import LazyGoogleMap from '../../components/map/LazyGoogleMap';
import { safeJsonFetch, authHeaders } from '../../lib/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminParcelData = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState({});
  const [fetchProgress, setFetchProgress] = useState({});  // Track progress for each county
  const [fetchingState, setFetchingState] = useState({});
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('auto-fetch');
  const [expandedStates, setExpandedStates] = useState({});
  const [filterState, setFilterState] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);  // Upload progress percentage
  const [isUploading, setIsUploading] = useState(false);    // Upload in progress flag

  // County-ID normalization (handles parcels imported with legacy/short county_ids)
  const [unmappedDiagnostic, setUnmappedDiagnostic] = useState(null);
  const [loadingDiagnostic, setLoadingDiagnostic] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [showNormalizeModal, setShowNormalizeModal] = useState(false);

  // Geo-data repair (handles parcels imported without `location` GeoJSON field)
  const [geoDiagnostic, setGeoDiagnostic] = useState(null);
  const [loadingGeoDiagnostic, setLoadingGeoDiagnostic] = useState(false);
  const [repairingGeo, setRepairingGeo] = useState(false);
  const [showGeoRepairModal, setShowGeoRepairModal] = useState(false);
  const [geoRepairProgress, setGeoRepairProgress] = useState(null);

  // Auto-run dry-run diagnostics on page load to surface real numbers in banners
  const [autoGeoDryRun, setAutoGeoDryRun] = useState(null);  // {docs_needing_location_backfill, has_2dsphere_index, ...}

  // Test-on-Map modal — preview a county's parcels on a leaflet map
  const [testMapCounty, setTestMapCounty] = useState(null);   // {county_id, county_name}
  const [testMapBounds, setTestMapBounds] = useState(null);   // {bounds, center, parcel_count}
  const [loadingTestMap, setLoadingTestMap] = useState(false);

  const fetchStatus = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    const url = `${API}/api/admin/usa-parcel-data/status${forceRefresh ? '?force_refresh=true' : ''}`;
    const res = await safeJsonFetch(url, { headers: authHeaders(), timeout: 60000 });
    if (res.ok && res.data) {
      // If status came back but with 0 counties (timeout-style empty result),
      // overlay the static config so the page still renders 24 county cards.
      const total = res.data._total || {};
      if ((total.total_counties || 0) === 0) {
        const fallback = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/config`, {
          headers: authHeaders(),
          timeout: 15000,
        });
        if (fallback.ok && fallback.data) {
          setStatus({
            ...fallback.data,
            _total: { ...fallback.data._total, status_fallback: true },
            _warning: 'Live counts unavailable (status API empty) — showing static county list. Click Refresh to retry.',
          });
        } else {
          setStatus(res.data);
        }
      } else {
        setStatus(res.data);
      }
    } else {
      // Hard failure on /status — try /config so user at least sees the 24 county tiles.
      const fallback = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/config`, {
        headers: authHeaders(),
        timeout: 15000,
      });
      if (fallback.ok && fallback.data) {
        setStatus({
          ...fallback.data,
          _total: { ...fallback.data._total, status_fallback: true },
          _error: res.error || `API returned ${res.status}`,
        });
      } else {
        setStatus({
          _total: { total_parcels: 0, counties_with_data: 0, total_counties: 0 },
          counties: {},
          states: {},
          _error: res.error || `API returned ${res.status}`,
        });
      }
    }
    setLoading(false);
  }, []);

  // Poll for fetch progress
  const pollFetchProgress = useCallback(async (countyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/fetch-progress/${countyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const progress = await response.json();
        setFetchProgress(prev => ({ ...prev, [countyId]: progress }));
        
        // If still in progress, continue polling
        if (progress.status === 'in_progress') {
          setTimeout(() => pollFetchProgress(countyId), 2000);
        } else {
          // Fetch completed or failed
          setFetching(prev => ({ ...prev, [countyId]: false }));
          fetchStatus();  // Refresh the main status
          
          if (progress.status === 'completed') {
            setMessage({ 
              type: 'success', 
              text: `Fetched ${progress.fetched_count.toLocaleString()} parcels for ${progress.county_name}` 
            });
          } else if (progress.status === 'failed') {
            setMessage({ 
              type: 'error', 
              text: `Fetch failed: ${progress.error_message || 'Unknown error'}` 
            });
          }
        }
      }
    } catch (error) {
      console.error('Error polling progress:', error);
    }
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 30 seconds when fetching
    const interval = setInterval(() => {
      if (Object.values(fetching).some(f => f) || Object.values(fetchingState).some(f => f)) {
        fetchStatus();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetching, fetchingState]);

  // Auto-Heal — sophisticated one-click healer that ensures all indexes and location backfill
  const [autoHealing, setAutoHealing] = useState(false);
  const [autoHealProgress, setAutoHealProgress] = useState(null);

  // Auto-run a dry-run geo diagnostic on page load so banners show real numbers.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API}/api/admin/usa-parcel-data/repair-geo-data`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ dry_run: true, batch_size: 5000 })
        });
        const rawText = await r.text();
        if (r.ok && !cancelled) {
          try {
            setAutoGeoDryRun(JSON.parse(rawText));
          } catch {
            // Non-JSON response (e.g. HTML error page from proxy); ignore.
          }
        }
      } catch (e) {
        // Non-fatal; the banner just falls back to its default messaging
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAutoFetch = async (countyId) => {
    setFetching(prev => ({ ...prev, [countyId]: true }));
    setFetchProgress(prev => ({ ...prev, [countyId]: { status: 'starting', fetched_count: 0 } }));
    setMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/auto-fetch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          county_id: countyId,
          max_parcels: 50000,
          residential_only: true
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        setMessage({ 
          type: 'info', 
          text: `Fetching parcels for ${result.county_name}... Progress will update automatically.` 
        });
        // Start polling for progress updates
        setTimeout(() => pollFetchProgress(countyId), 2000);
      } else {
        setMessage({ type: 'error', text: result.detail || 'Auto-fetch failed' });
        setFetching(prev => ({ ...prev, [countyId]: false }));
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.message}` });
      setFetching(prev => ({ ...prev, [countyId]: false }));
    }
  };

  const handleFetchState = async (state) => {
    if (!window.confirm(`This will fetch parcel data from all counties in ${state}. This may take a while. Continue?`)) {
      return;
    }

    setFetchingState(prev => ({ ...prev, [state]: true }));
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/auto-fetch-by-state/${state}?max_per_county=50000`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      
      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: result.message 
        });
      } else {
        setMessage({ type: 'error', text: result.detail || 'Auto-fetch failed' });
        setFetchingState(prev => ({ ...prev, [state]: false }));
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.message}` });
      setFetchingState(prev => ({ ...prev, [state]: false }));
    }
  };

  const handleTestApi = async (countyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/test-api/${countyId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: `API test successful for ${result.name}! Sample data retrieved.` 
        });
      } else {
        setMessage({ type: 'error', text: `API test failed: ${result.error}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.message}` });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedCounty || !importFile) {
      setMessage({ type: 'error', text: 'Please select a county and upload a file' });
      return;
    }

    const token = localStorage.getItem('token');
    const fileSize = importFile.size;
    const fileName = importFile.name;  // Store name before clearing
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks for fewer HTTP requests
    
    setIsUploading(true);
    setUploadProgress(0);
    setMessage({ type: 'info', text: 'Preparing upload...' });
    
    let backgroundProcessing = false; // Track if we started background processing

    try {
      // For files under 50MB, use direct upload (faster + no chunk loss risk)
      if (fileSize < 50 * 1024 * 1024) {
        const result = await uploadFileWithProgress(importFile, selectedCounty, token);
        if (result?.processing) backgroundProcessing = true;
      } else {
        // For large files, use chunked upload
        const result = await uploadLargeFile(importFile, selectedCounty, token, CHUNK_SIZE);
        if (result?.processing) backgroundProcessing = true;
      }
    } catch (error) {
      // Extract just the error message string to avoid serialization issues
      const errorMsg = error?.message || String(error) || 'Unknown error occurred';
      setMessage({ type: 'error', text: `Import error: ${errorMsg}` });
    } finally {
      // Only reset isUploading if NOT doing background processing
      // Background processing will reset it when complete via polling
      if (!backgroundProcessing) {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };

  // Upload small file with XMLHttpRequest for progress tracking
  const uploadFileWithProgress = (file, countyId, token) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('county_id', countyId);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percent);
          setMessage({ type: 'info', text: `Uploading: ${percent}%` });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText);
            // Extract only primitive values for state
            const importedCount = result.imported_count || 0;
            const formatDetected = result.format_detected || 'file';
            const countyName = result.county_name || countyId;
            
            setMessage({ 
              type: 'success', 
              text: `Successfully imported ${importedCount.toLocaleString()} parcels from ${formatDetected} for ${countyName}. Refreshing...` 
            });
            setImportFile(null);
            setSelectedCounty(null);
            setUploadProgress(100);
            
            // Delay to ensure DB committed, then refresh
            setTimeout(async () => {
              await fetchStatus();
              setMessage({ 
                type: 'success', 
                text: `Successfully imported ${importedCount.toLocaleString()} parcels from ${formatDetected} for ${countyName}` 
              });
            }, 500);
            
            resolve({ success: true, imported_count: importedCount });
          } catch (e) {
            reject(new Error('Invalid server response'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.detail || `Upload failed (${xhr.status})`));
          } catch (e) {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload timed out'));
      });

      xhr.open('POST', `${API}/api/admin/usa-parcel-data/import-file`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.timeout = 300000; // 5 minute timeout
      xhr.send(formData);
    });
  };

  // Upload large file in chunks using XMLHttpRequest (not fetch)
  const uploadLargeFile = async (file, countyId, token, chunkSize) => {
    const totalChunks = Math.ceil(file.size / chunkSize);
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fileName = file.name;
    const MAX_RETRIES = 5; // 5 retry attempts per chunk
    const BATCH_SIZE = 50; // Process in batches of 50 chunks
    const BATCH_DELAY = 500; // 500ms delay between batches to prevent server overload
    
    // Store upload state in sessionStorage for resume capability
    const uploadStateKey = `upload_${countyId}_${fileName}`;
    let startChunk = 0;
    
    // Check for existing upload state (for resume)
    const savedState = sessionStorage.getItem(uploadStateKey);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        if (state.uploadId && state.lastChunk < totalChunks - 1) {
          // Offer to resume
          const shouldResume = window.confirm(
            `Found incomplete upload at ${Math.round((state.lastChunk / totalChunks) * 100)}%. Resume from chunk ${state.lastChunk + 1}?`
          );
          if (shouldResume) {
            startChunk = state.lastChunk + 1;
            setMessage({ type: 'info', text: `Resuming upload from chunk ${startChunk + 1}...` });
          } else {
            sessionStorage.removeItem(uploadStateKey);
          }
        }
      } catch (e) {
        sessionStorage.removeItem(uploadStateKey);
      }
    }
    
    setMessage({ type: 'info', text: `Uploading large file in ${totalChunks} chunks (in batches of ${BATCH_SIZE})...` });
    
    // Upload each chunk using XMLHttpRequest with retry logic and batching
    for (let chunkIndex = startChunk; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      
      let lastError = null;
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          await new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('chunk', chunk, fileName);
            formData.append('chunk_index', chunkIndex.toString());
            formData.append('total_chunks', totalChunks.toString());
            formData.append('upload_id', uploadId);
            formData.append('county_id', countyId);
            formData.append('filename', fileName);
            formData.append('file_size', file.size.toString());
            
            const xhr = new XMLHttpRequest();
            
            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
              } else {
                try {
                  const errorData = JSON.parse(xhr.responseText);
                  reject(new Error(errorData.detail || `Chunk ${chunkIndex + 1} upload failed`));
                } catch (e) {
                  reject(new Error(`Chunk ${chunkIndex + 1} upload failed with status ${xhr.status}`));
                }
              }
            });
            
            xhr.addEventListener('error', () => {
              reject(new Error(`Network error uploading chunk ${chunkIndex + 1}`));
            });
            
            xhr.addEventListener('timeout', () => {
              reject(new Error(`Timeout uploading chunk ${chunkIndex + 1}`));
            });
            
            xhr.open('POST', `${API}/api/admin/usa-parcel-data/upload-chunk`);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.timeout = 120000; // 2 minute timeout per chunk
            xhr.send(formData);
          });
          lastError = null;
          break; // Success, exit retry loop
        } catch (error) {
          lastError = error;
          console.warn(`Chunk ${chunkIndex + 1} attempt ${attempt + 1} failed:`, error.message);
          if (attempt < MAX_RETRIES - 1) {
            // Wait before retrying (exponential backoff)
            const waitTime = Math.pow(2, attempt) * 1000;
            setMessage({ type: 'info', text: `Retrying chunk ${chunkIndex + 1} in ${waitTime/1000}s... (attempt ${attempt + 2}/${MAX_RETRIES})` });
            await new Promise(r => setTimeout(r, waitTime));
          }
        }
      }
      
      if (lastError) {
        // Save state for resume before throwing
        sessionStorage.setItem(uploadStateKey, JSON.stringify({
          uploadId,
          lastChunk: chunkIndex - 1,
          totalChunks,
          fileName
        }));
        throw lastError; // All retries failed
      }
      
      // Save progress state every 10 chunks for resume capability
      if ((chunkIndex + 1) % 10 === 0) {
        sessionStorage.setItem(uploadStateKey, JSON.stringify({
          uploadId,
          lastChunk: chunkIndex,
          totalChunks,
          fileName
        }));
      }
      
      const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
      setUploadProgress(progress);
      
      // Every batch, add a small delay to let server breathe
      if ((chunkIndex + 1) % BATCH_SIZE === 0 && chunkIndex + 1 < totalChunks) {
        const batchNum = Math.floor((chunkIndex + 1) / BATCH_SIZE);
        const totalBatches = Math.ceil(totalChunks / BATCH_SIZE);
        setMessage({ type: 'info', text: `Batch ${batchNum}/${totalBatches} complete (${progress}%). Preparing next batch...` });
        await new Promise(r => setTimeout(r, BATCH_DELAY));
      } else {
        setMessage({ type: 'info', text: `Uploading: ${progress}% (chunk ${chunkIndex + 1}/${totalChunks})` });
      }
    }
    
    // Clear saved state on successful completion
    sessionStorage.removeItem(uploadStateKey);
    
    // Finalize the upload - process the assembled file
    setMessage({ type: 'info', text: 'Processing uploaded file...' });
    
    // Use XMLHttpRequest for finalize too
    const result = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (e) {
            reject(new Error('Invalid server response'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.detail || 'Failed to process uploaded file'));
          } catch (e) {
            reject(new Error(`Processing failed with status ${xhr.status}`));
          }
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during processing'));
      });
      
      xhr.addEventListener('timeout', () => {
        reject(new Error('Processing timed out'));
      });
      
      xhr.open('POST', `${API}/api/admin/usa-parcel-data/finalize-upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = 30000; // 30 second timeout - backend now processes in background
      xhr.send(JSON.stringify({
        upload_id: uploadId,
        county_id: countyId,
        filename: fileName
      }));
    });
    
    // Check if processing is happening in background
    if (result.processing && result.process_id) {
      const countyName = result.county_name || countyId;
      setMessage({ 
        type: 'info', 
        text: `Processing ${fileName} in background. This may take several minutes for large files...` 
      });
      
      // Keep isUploading true during background processing to show progress bar
      // Don't reset it until processing is complete
      
      // Poll for status updates
      const pollStatus = async () => {
        try {
          const statusResponse = await fetch(`${API}/api/admin/usa-parcel-data/import-status/${result.process_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            setUploadProgress(statusData.progress || 0);
            
            if (statusData.status === 'complete') {
              setMessage({ 
                type: 'success', 
                text: `Successfully imported ${(statusData.imported_count || 0).toLocaleString()} parcels for ${countyName}` 
              });
              setImportFile(null);
              setSelectedCounty(null);
              setIsUploading(false); // Now we can stop showing progress
              await fetchStatus();
              return; // Stop polling
            } else if (statusData.status === 'error') {
              setMessage({ type: 'error', text: statusData.message || 'Import failed' });
              setImportFile(null);
              setIsUploading(false); // Stop showing progress on error
              return; // Stop polling
            } else {
              // Still processing - update message and continue polling
              setMessage({ type: 'info', text: statusData.message || 'Processing...' });
              setTimeout(pollStatus, 3000); // Poll every 3 seconds
            }
          } else {
            // If status check fails, retry
            setTimeout(pollStatus, 5000);
          }
        } catch (e) {
          console.error('Error polling status:', e);
          setTimeout(pollStatus, 5000); // Retry in 5 seconds on error
        }
      };
      
      // Start polling after a short delay
      setTimeout(pollStatus, 2000);
      return { processing: true, process_id: result.process_id }; // Signal that background processing started
    }
    
    // Old synchronous handling (for smaller files that complete immediately)
    const importedCount = result.imported_count || 0;
    const countyName = result.county_name || countyId;
    
    setMessage({ 
      type: 'success', 
      text: `Successfully imported ${importedCount.toLocaleString()} parcels for ${countyName}. Refreshing data...` 
    });
    setImportFile(null);
    setSelectedCounty(null);
    setUploadProgress(100);
    
    // Small delay to ensure DB has committed, then refresh status
    setTimeout(async () => {
      await fetchStatus();
      setMessage({ 
        type: 'success', 
        text: `Successfully imported ${importedCount.toLocaleString()} parcels for ${countyName}` 
      });
    }, 500);
    
    return { success: true, imported_count: importedCount };
  };

  const handleClearCounty = async (countyId) => {
    const countyInfo = status?.counties?.[countyId];
    if (!window.confirm(`Are you sure you want to delete all parcel data for ${countyInfo?.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/${countyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ 
          type: 'success', 
          text: `Deleted ${result.deleted_count.toLocaleString()} parcels from ${countyId}` 
        });
        fetchStatus();
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Delete error: ${error.message}` });
    }
  };

  const toggleStateExpansion = (state) => {
    setExpandedStates(prev => ({
      ...prev,
      [state]: !prev[state]
    }));
  };

  // Test-on-Map: fetch the county's bounding box and show a map preview
  const handleTestOnMap = async (countyId, countyName) => {
    setTestMapCounty({ county_id: countyId, county_name: countyName });
    setTestMapBounds(null);
    setLoadingTestMap(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/county-bounds/${countyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.has_data) {
          setTestMapBounds(data);
        } else {
          setMessage({ type: 'info', text: data.message || 'No parcel data found for this county' });
          setTestMapCounty(null);
        }
      } else {
        setMessage({ type: 'error', text: `Could not load county bounds: HTTP ${response.status}` });
        setTestMapCounty(null);
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Test on Map error: ${error.message}` });
      setTestMapCounty(null);
    } finally {
      setLoadingTestMap(false);
    }
  };

  // Diagnose unmapped county_ids (parcels imported with legacy/short keys)
  const handleDiagnoseUnmapped = async () => {
    setLoadingDiagnostic(true);
    setShowNormalizeModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/unmapped-county-ids`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setUnmappedDiagnostic(await response.json());
      } else {
        setMessage({ type: 'error', text: `Diagnostic failed: HTTP ${response.status}` });
        setShowNormalizeModal(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Diagnostic failed: ${error.message}` });
      setShowNormalizeModal(false);
    } finally {
      setLoadingDiagnostic(false);
    }
  };

  // Run the normalization (renames legacy county_ids to canonical USA_PARCEL_SOURCES keys)
  const handleNormalizeCountyIds = async () => {
    if (!window.confirm('Rename all legacy county_id values to their canonical form? This updates documents in MongoDB and cannot be undone automatically.')) {
      return;
    }
    setNormalizing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/normalize-county-ids`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: false })
      });
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message || 'Normalization complete' });
        setShowNormalizeModal(false);
        setUnmappedDiagnostic(null);
        fetchStatus();
      } else {
        setMessage({ type: 'error', text: `Normalization failed: HTTP ${response.status}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Normalization failed: ${error.message}` });
    } finally {
      setNormalizing(false);
    }
  };

  // Diagnose geo-data health (location field + 2dsphere index)
  const handleDiagnoseGeo = async () => {
    setLoadingGeoDiagnostic(true);
    setShowGeoRepairModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/geo-diagnostic`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const rawText = await response.text();
      if (response.ok) {
        try {
          setGeoDiagnostic(JSON.parse(rawText));
        } catch {
          setMessage({ type: 'error', text: `Geo diagnostic returned non-JSON: ${rawText.slice(0, 200)}` });
          setShowGeoRepairModal(false);
        }
      } else {
        setMessage({ type: 'error', text: `Geo diagnostic failed: HTTP ${response.status} ${rawText.slice(0, 100)}` });
        setShowGeoRepairModal(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Geo diagnostic failed: ${error.message}` });
      setShowGeoRepairModal(false);
    } finally {
      setLoadingGeoDiagnostic(false);
    }
  };

  // Run geo-data repair (backfill location + create 2dsphere index) — runs in
  // background on server; we poll status to survive proxy timeouts on millions of rows.
  const handleRepairGeoData = async () => {
    if (!window.confirm('Backfill the `location` GeoJSON field on all parcels and ensure the 2dsphere index exists? Runs in background; you can keep this window open and watch progress.')) {
      return;
    }
    setRepairingGeo(true);
    setGeoRepairProgress({ status: 'starting', progress: 0, message: 'Starting repair...' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/usa-parcel-data/repair-geo-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: false, batch_size: 5000 })
      });
      // Read body exactly once as text to avoid "body stream already read" issues
      // when both success and error branches need to inspect the payload.
      const rawText = await response.text();
      if (!response.ok) {
        setMessage({ type: 'error', text: `Geo repair failed to start: HTTP ${response.status} - ${rawText.slice(0, 200)}` });
        setRepairingGeo(false);
        setGeoRepairProgress(null);
        return;
      }
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (parseErr) {
        setMessage({ type: 'error', text: `Repair endpoint returned non-JSON: ${rawText.slice(0, 200)}` });
        setRepairingGeo(false);
        setGeoRepairProgress(null);
        return;
      }
      const processId = data.process_id;
      if (!processId) {
        setMessage({ type: 'error', text: 'Repair endpoint did not return a process_id' });
        setRepairingGeo(false);
        setGeoRepairProgress(null);
        return;
      }

      // Poll status every 3s until completed/failed
      setGeoRepairProgress({
        status: 'running',
        progress: 0,
        total_to_backfill: data.total_to_backfill,
        message: data.message,
        process_id: processId
      });

      const pollInterval = 3000;
      const maxAttempts = 600; // 30 min cap
      let attempts = 0;

      const poll = async () => {
        attempts += 1;
        try {
          const sResp = await fetch(`${API}/api/admin/usa-parcel-data/repair-geo-data/status/${processId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const sText = await sResp.text();
          if (!sResp.ok) {
            throw new Error(`status HTTP ${sResp.status} ${sText.slice(0, 100)}`);
          }
          let sData;
          try {
            sData = JSON.parse(sText);
          } catch (parseErr) {
            throw new Error('Status endpoint returned non-JSON');
          }
          setGeoRepairProgress(sData);
          if (sData.status === 'completed') {
            setMessage({ type: 'success', text: sData.message || 'Geo data repair complete' });
            setRepairingGeo(false);
            setTimeout(() => {
              setShowGeoRepairModal(false);
              setGeoDiagnostic(null);
              setGeoRepairProgress(null);
              fetchStatus();
            }, 1500);
            return;
          }
          if (sData.status === 'failed') {
            setMessage({ type: 'error', text: sData.message || 'Geo data repair failed' });
            setRepairingGeo(false);
            return;
          }
          if (attempts >= maxAttempts) {
            setMessage({ type: 'error', text: 'Polling timed out after 30 minutes — repair may still be running. Refresh the page to check.' });
            setRepairingGeo(false);
            return;
          }
          setTimeout(poll, pollInterval);
        } catch (e) {
          // Retry on transient errors
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setMessage({ type: 'error', text: `Lost connection while polling: ${e.message}` });
            setRepairingGeo(false);
          }
        }
      };
      setTimeout(poll, pollInterval);
    } catch (error) {
      setMessage({ type: 'error', text: `Geo repair failed: ${error.message}` });
      setRepairingGeo(false);
      setGeoRepairProgress(null);
    }
  };

  // Sophisticated one-click Auto-Heal: ensures all indexes (county_id, latitude+longitude,
  // 2dsphere on location) exist, backfills the location field, and validates with a real
  // $geoWithin query. This is the recommended healer for production-scale parcel data.
  const handleAutoHeal = async () => {
    if (!window.confirm('Run Auto-Heal Parcel Database?\n\nThis will:\n  1. Create the county_id index\n  2. Create the lat/lng compound index\n  3. Backfill the `location` GeoJSON field\n  4. Create the 2dsphere index on `location` (the critical fix)\n  5. Validate with a real $geoWithin query\n\nRuns in background. Safe to keep working. Large datasets (millions of rows) may take 3-10 minutes.')) {
      return;
    }
    setAutoHealing(true);
    setAutoHealProgress({ status: 'starting', progress: 0, step: 'starting', message: 'Starting Auto-Heal pipeline...' });

    const res = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/auto-heal`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ dry_run: false }),
    });
    if (!res.ok) {
      setMessage({ type: 'error', text: `Auto-Heal failed to start: ${res.error}` });
      setAutoHealing(false);
      setAutoHealProgress(null);
      return;
    }
    const processId = res.data?.process_id;
    if (!processId) {
      setMessage({ type: 'error', text: 'Auto-Heal did not return a process_id' });
      setAutoHealing(false);
      setAutoHealProgress(null);
      return;
    }
    setAutoHealProgress({ status: 'running', progress: 0, step: 'starting', message: res.data.message, process_id: processId });

    const pollInterval = 3000;
    const maxAttempts = 600;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      const sRes = await safeJsonFetch(`${API}/api/admin/usa-parcel-data/repair-geo-data/status/${processId}`, {
        headers: authHeaders(),
      });
      if (sRes.ok && sRes.data) {
        setAutoHealProgress(sRes.data);
        if (sRes.data.status === 'completed') {
          setMessage({ type: sRes.data.validation_ok ? 'success' : 'info', text: sRes.data.message || 'Auto-Heal complete' });
          setAutoHealing(false);
          setTimeout(() => { setAutoHealProgress(null); fetchStatus(); }, 3000);
          return;
        }
        if (sRes.data.status === 'failed') {
          setMessage({ type: 'error', text: sRes.data.message || 'Auto-Heal failed' });
          setAutoHealing(false);
          return;
        }
      }
      if (attempts >= maxAttempts) {
        setMessage({ type: 'error', text: 'Auto-Heal polling timed out — refresh to see the latest state.' });
        setAutoHealing(false);
        return;
      }
      setTimeout(poll, pollInterval);
    };
    setTimeout(poll, pollInterval);
  };

  // Filter counties based on search and state filter
  const getFilteredCounties = () => {
    if (!status?.counties) return {};
    
    let filtered = { ...status.counties };
    
    // Filter by state
    if (filterState !== 'all') {
      filtered = Object.fromEntries(
        Object.entries(filtered).filter(([, config]) => config.state === filterState)
      );
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = Object.fromEntries(
        Object.entries(filtered).filter(([id, config]) => 
          config.name.toLowerCase().includes(query) ||
          config.city?.toLowerCase().includes(query) ||
          config.state?.toLowerCase().includes(query) ||
          id.toLowerCase().includes(query)
        )
      );
    }
    
    return filtered;
  };

  // Get unique states from status
  const getStates = () => {
    if (!status?.states) return [];
    return Object.keys(status.states).sort();
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const totalParcels = status?._total?.total_parcels || 0;
  const rawCollectionCount = status?._total?.raw_collection_count || 0;
  const displayParcels = Math.max(totalParcels, rawCollectionCount);
  const countiesWithData = status?._total?.counties_with_data || 0;
  const totalCounties = status?._total?.total_counties || 0;
  const filteredCounties = getFilteredCounties();

  return (
    <div className="space-y-6 p-6" data-testid="admin-parcel-data">
      {/* Warning banner — shown when status fell back to static config */}
      {(status?._warning || (status?._total?.status_fallback && !status?._error)) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="status-fallback-warning">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900 text-sm">Showing static county list (live counts unavailable)</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {status._warning || 'Status API returned no counties — likely a transient backend timeout. The parcel data still exists in the database. Click Refresh to retry.'}
            </p>
          </div>
          <Button onClick={() => fetchStatus(true)} size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Globe className="w-7 h-7 text-blue-600" />
            USA Parcel Data Management
          </h1>
          <p className="text-gray-500 mt-1">
            Import and manage residential parcel data from major US cities for accurate house counting
          </p>
        </div>
        <button
          onClick={() => fetchStatus(true)}
          disabled={loading}
          title="Force refresh — bypasses 60s server cache"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          data-testid="refresh-status-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
          {status?._total?.from_cache && (
            <span className="text-xs text-gray-500 ml-1">(cached {status._total.cache_age_sec}s)</span>
          )}
        </button>
      </div>

      {/* Alert Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${
          message.type === 'success' ? 'bg-green-50 border border-green-200' : 
          message.type === 'info' ? 'bg-blue-50 border border-blue-200' :
          'bg-red-50 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : message.type === 'info' ? (
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={
            message.type === 'success' ? 'text-green-800' : 
            message.type === 'info' ? 'text-blue-800' :
            'text-red-800'
          }>
            {message.text}
          </p>
        </div>
      )}

      {/* Summary KPI Cards — 4-tile health overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white" data-testid="kpi-total-parcels">
          <p className="text-blue-100 text-xs uppercase tracking-wide">Total Parcels</p>
          <p className="text-3xl font-bold mt-1">{displayParcels.toLocaleString()}</p>
          {rawCollectionCount > 0 && totalParcels === 0 && (
            <p className="text-blue-200 text-xs mt-1">Imported outside county config</p>
          )}
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white" data-testid="kpi-counties-imported">
          <p className="text-emerald-100 text-xs uppercase tracking-wide">Counties Imported</p>
          <p className="text-3xl font-bold mt-1">{countiesWithData} <span className="text-emerald-200 text-base font-normal">/ {totalCounties}</span></p>
        </div>
        <div
          className={`rounded-xl p-5 text-white ${(status?._total?.unmapped_parcels || 0) > 0 ? 'bg-gradient-to-br from-rose-500 to-rose-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`}
          data-testid="kpi-unmapped"
        >
          <p className="text-white/80 text-xs uppercase tracking-wide">Unmapped Parcels</p>
          <p className="text-3xl font-bold mt-1">{(status?._total?.unmapped_parcels || 0).toLocaleString()}</p>
          <p className="text-white/70 text-xs mt-1">{(status?._total?.unmapped_parcels || 0) > 0 ? 'Click Diagnose & Normalize' : 'All county_ids canonical'}</p>
        </div>
        <div
          className={`rounded-xl p-5 text-white ${
            autoGeoDryRun?.docs_needing_location_backfill > 0 || (autoGeoDryRun && !autoGeoDryRun.has_2dsphere_index)
              ? 'bg-gradient-to-br from-orange-500 to-orange-600'
              : autoGeoDryRun
                ? 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                : 'bg-gradient-to-br from-slate-400 to-slate-500'
          }`}
          data-testid="kpi-needs-repair"
        >
          <p className="text-white/80 text-xs uppercase tracking-wide">Needs Geo Repair</p>
          <p className="text-3xl font-bold mt-1">
            {autoGeoDryRun
              ? (autoGeoDryRun.docs_needing_location_backfill || 0).toLocaleString()
              : '...'}
          </p>
          <p className="text-white/70 text-xs mt-1">
            {autoGeoDryRun
              ? autoGeoDryRun.has_2dsphere_index
                ? (autoGeoDryRun.docs_needing_location_backfill > 0 ? 'Run repair below' : 'All clear')
                : 'Index missing — run repair'
              : 'Analyzing...'}
          </p>
        </div>
      </div>

      {/* Unmapped County IDs Banner — shows when parcels exist in DB but have legacy/unrecognized county_id values */}
      {(() => {
        const unmapped = status?._total?.unmapped_parcels || 0;
        if (unmapped <= 0) return null;
        return (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-center justify-between" data-testid="unmapped-county-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="font-semibold text-rose-900 text-sm">
                  {unmapped.toLocaleString()} parcels have legacy county_id values
                </p>
                <p className="text-xs text-rose-700">
                  These parcels are in the database but their <code>county_id</code> doesn&apos;t match the configured keys (e.g. <code>&quot;harris&quot;</code> instead of <code>&quot;harris_tx&quot;</code>), so per-county coverage shows 0%. Geo-based house counting still works, but the dashboard is misleading.
                </p>
              </div>
            </div>
            <Button
              onClick={handleDiagnoseUnmapped}
              disabled={loadingDiagnostic}
              className="bg-rose-500 hover:bg-rose-600 text-white text-sm whitespace-nowrap"
              data-testid="diagnose-unmapped-btn"
            >
              {loadingDiagnostic ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Diagnose &amp; Normalize
            </Button>
          </div>
        );
      })()}

      {/* Geo Data Health Banner — shows actual numbers via auto dry-run on load.
          Always renders the Auto-Heal button (disabled when clean) so it's
          discoverable + testable, instead of hiding the whole banner. */}
      {(() => {
        const totalRaw = (status?._total?.raw_collection_count || 0);
        if (totalRaw <= 0) return null;
        const dryRun = autoGeoDryRun;
        const docsNeeding = dryRun?.docs_needing_location_backfill || 0;
        const indexMissing = dryRun ? !dryRun.has_2dsphere_index : false;
        const needsRepair = docsNeeding > 0 || indexMissing;
        const allClear = dryRun && !needsRepair;
        return (
          <div
            className={`${allClear ? 'bg-emerald-50 border-emerald-200' : needsRepair ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'} border rounded-xl p-4 flex items-center justify-between`}
            data-testid="geo-health-banner"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${allClear ? 'bg-emerald-100' : needsRepair ? 'bg-orange-100' : 'bg-slate-100'} rounded-lg flex items-center justify-center`}>
                <Zap className={`w-5 h-5 ${allClear ? 'text-emerald-600' : needsRepair ? 'text-orange-600' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className={`font-semibold text-sm ${allClear ? 'text-emerald-900' : needsRepair ? 'text-orange-900' : 'text-slate-900'}`}>
                  {dryRun
                    ? allClear
                      ? 'Parcel database is healthy ✓'
                      : indexMissing
                        ? '2dsphere index is missing — Count Houses cannot work without it'
                        : docsNeeding > 0
                          ? `${docsNeeding.toLocaleString()} parcels need geo repair`
                          : 'Parcel Database needs healing'
                    : 'Analyzing parcel database health…'}
                </p>
                <p className={`text-xs ${allClear ? 'text-emerald-700' : needsRepair ? 'text-orange-700' : 'text-slate-600'}`}>
                  {allClear
                    ? 'All indexes present, location field populated everywhere. You can still re-run Auto-Heal at any time as a safety check.'
                    : 'Run Auto-Heal Parcel Database to create all required indexes, backfill the location field, and validate with a test query. Sophisticated one-click healer; runs in background.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button
                onClick={handleAutoHeal}
                disabled={autoHealing}
                className={`${needsRepair ? 'bg-orange-600 hover:bg-orange-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white text-sm whitespace-nowrap shadow-md`}
                data-testid="auto-heal-btn"
              >
                {autoHealing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                {needsRepair ? 'Auto-Heal Parcel Database' : 'Re-Run Auto-Heal'}
              </Button>
              <Button
                onClick={handleDiagnoseGeo}
                disabled={loadingGeoDiagnostic}
                variant="outline"
                className={`${needsRepair ? 'text-orange-700 border-orange-300' : 'text-emerald-700 border-emerald-300'} text-sm whitespace-nowrap`}
                data-testid="diagnose-geo-btn"
              >
                Advanced
              </Button>
            </div>
          </div>
        );
      })()}

      {/* Auto-Heal Progress Card — visible while a pipeline is running */}
      {autoHealing && autoHealProgress && (
        <div className="bg-white border-2 border-orange-200 rounded-xl p-5 shadow-sm" data-testid="auto-heal-progress">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-600" />
              <h3 className="font-semibold text-gray-900">Auto-Heal in progress...</h3>
            </div>
            <span className="text-sm font-bold text-orange-700 tabular-nums">{Math.min(autoHealProgress.progress || 0, 100)}%</span>
          </div>
          <div className="w-full h-3 bg-orange-100 rounded-full overflow-hidden mb-3">
            <div
              className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500 ease-out"
              style={{ width: `${Math.min(autoHealProgress.progress || 0, 100)}%` }}
            />
          </div>
          <p className="text-sm text-gray-700 font-medium" data-testid="auto-heal-current-message">
            {autoHealProgress.message || 'Working...'}
          </p>
          {Array.isArray(autoHealProgress.steps) && autoHealProgress.steps.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs">
              {autoHealProgress.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={`mt-0.5 ${
                    s.status === 'ok' ? 'text-emerald-600' :
                    s.status === 'error' ? 'text-rose-600' :
                    s.status === 'warning' ? 'text-amber-600' :
                    'text-blue-500'
                  }`}>
                    {s.status === 'ok' ? '✓' : s.status === 'error' ? '✗' : s.status === 'warning' ? '⚠' : '•'}
                  </span>
                  <span className="text-gray-700"><strong>{s.name}</strong> — {s.message}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-gray-500">
            Safe to keep this tab open or come back later. The pipeline runs in the background on the server.
          </p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('auto-fetch')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'auto-fetch'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Auto-Fetch (Recommended)
          </button>
          <button
            onClick={() => setActiveTab('by-state')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'by-state'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Map className="w-4 h-4 inline mr-2" />
            Browse by State
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'manual'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            Manual Import
          </button>
        </nav>
      </div>

      {/* Auto-Fetch Tab */}
      {activeTab === 'auto-fetch' && (
        <div className="space-y-6">
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search counties by name, city, or state..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="county-search-input"
              />
            </div>
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="state-filter-select"
            >
              <option value="all">All States</option>
              {getStates().map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          {/* County Cards for Auto-Fetch */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(filteredCounties).map(([countyId, county]) => {
              const isFetching = fetching[countyId] || false;
              const progress = fetchProgress[countyId];
              const hasData = county.parcels_in_database > 0;
              const hasApi = county.has_api;
              
              return (
                <div key={countyId} className={`bg-white rounded-xl border p-5 space-y-4 ${
                  isFetching ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-200'
                }`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{county.name}</h3>
                      <p className="text-sm text-gray-500">
                        {isFetching && progress?.fetched_count > 0 
                          ? `${progress.fetched_count.toLocaleString()} fetched...`
                          : `${county.parcels_in_database?.toLocaleString() || 0} parcels`
                        }
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      {isFetching ? (
                        <Badge className="bg-blue-100 text-blue-700 animate-pulse">Fetching...</Badge>
                      ) : hasData ? (
                        <Badge className="bg-green-100 text-green-700">Imported</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600">Not Imported</Badge>
                      )}
                      {!hasApi && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Manual Only</Badge>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar - shows fetch progress when fetching */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{isFetching ? 'Fetch Progress' : 'Coverage'}</span>
                      <span>
                        {isFetching && progress?.target_count > 0
                          ? `${progress.progress_percent || 0}%`
                          : `${county.coverage_percent?.toFixed(1) || 0}%`
                        }
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          isFetching ? 'bg-blue-500 animate-pulse' : 'bg-blue-500'
                        }`}
                        style={{ 
                          width: `${Math.min(
                            isFetching && progress?.progress_percent 
                              ? progress.progress_percent 
                              : county.coverage_percent || 0, 
                            100
                          )}%` 
                        }}
                      />
                    </div>
                    {/* Live count while fetching */}
                    {isFetching && progress?.fetched_count > 0 && (
                      <p className="text-xs text-blue-600 font-medium">
                        {progress.fetched_count.toLocaleString()} / {progress.target_count?.toLocaleString() || '?'} parcels
                      </p>
                    )}
                  </div>

                  {/* Last Fetch Info */}
                  {!isFetching && county.last_fetch && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Last fetch: {new Date(county.last_fetch).toLocaleDateString()}
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {hasApi ? (
                      <>
                        <Button
                          onClick={() => handleAutoFetch(countyId)}
                          disabled={isFetching}
                          size="sm"
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`fetch-${countyId}-btn`}
                        >
                          {isFetching ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fetching...</>
                          ) : (
                            <><Play className="w-3 h-3 mr-1" /> {hasData ? 'Refresh' : 'Fetch'}</>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleTestApi(countyId)}
                          size="sm"
                          variant="outline"
                          className="text-gray-600"
                          data-testid={`test-${countyId}-btn`}
                        >
                          Test
                        </Button>
                      </>
                    ) : (
                      <a
                        href={county.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" /> Download Data
                        </Button>
                      </a>
                    )}
                    {hasData && (
                      <Button
                        onClick={() => handleClearCounty(countyId)}
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`delete-${countyId}-btn`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {hasData ? (
                    <Button
                      onClick={() => navigate(`/admin/parcel-data/county/${countyId}`)}
                      size="sm"
                      className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid={`manage-${countyId}-btn`}
                    >
                      <Database className="w-3 h-3 mr-1" /> Manage County
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate(`/admin/parcel-data/county/${countyId}`)}
                      size="sm"
                      variant="outline"
                      className="w-full mt-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                      data-testid={`manage-${countyId}-btn`}
                      title="Open the per-county page — works even before any data is imported"
                    >
                      <Database className="w-3 h-3 mr-1" /> Open County Page
                    </Button>
                  )}
                  {hasData && (
                    <Button
                      onClick={() => handleTestOnMap(countyId, county.name)}
                      size="sm"
                      variant="outline"
                      className="w-full mt-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                      data-testid={`test-on-map-${countyId}-btn`}
                    >
                      <Map className="w-3 h-3 mr-1" /> Test on Map
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {Object.keys(filteredCounties).length === 0 && (
            <div className="text-center py-12 text-gray-500" data-testid="parcel-empty-state">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              {status?._error ? (
                <div className="space-y-3 max-w-xl mx-auto">
                  <p className="font-medium text-red-600">Could not load parcel data status</p>
                  <p className="text-sm text-gray-700">
                    The backend returned an error:&nbsp;
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs">{String(status._error).slice(0, 200)}</code>
                  </p>
                  <p className="text-xs text-gray-500">
                    First, run this in a new tab to confirm the backend is healthy:&nbsp;
                    <code className="bg-gray-100 px-1 rounded">/api/health/parcel-config</code>.
                    If that returns <code>county_count: 24</code>, click <strong>Refresh</strong> in the top-right.
                  </p>
                  <Button onClick={() => fetchStatus(true)} className="mt-2">
                    <RefreshCw className="w-4 h-4 mr-2" /> Force Refresh
                  </Button>
                </div>
              ) : totalCounties === 0 ? (
                <div className="space-y-3 max-w-xl mx-auto">
                  <p className="font-medium text-red-600">No counties returned by the status API</p>
                  <p className="text-sm text-gray-700">
                    The backend either timed out or returned an empty list. The parcel data probably exists — this is a display-only issue.
                  </p>
                  <p className="text-xs text-gray-500">
                    Diagnostic: open&nbsp;
                    <code className="bg-gray-100 px-1 rounded">/api/health/parcel-config</code>&nbsp;
                    in a new tab. If it shows <code>parcels_in_db &gt; 0</code> and <code>county_count: 24</code>, click Refresh below to force a cache bypass.
                  </p>
                  <Button onClick={() => fetchStatus(true)} className="mt-2">
                    <RefreshCw className="w-4 h-4 mr-2" /> Force Refresh (bypass cache)
                  </Button>
                </div>
              ) : (
                <p>No counties found matching your search criteria.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Browse by State Tab */}
      {activeTab === 'by-state' && (
        <div className="space-y-4">
          {getStates().map(state => {
            const stateCounties = status?.states?.[state] || [];
            const isExpanded = expandedStates[state];
            const stateParcels = stateCounties.reduce((sum, c) => {
              const countyData = status?.counties?.[c.id];
              return sum + (countyData?.parcels_in_database || 0);
            }, 0);
            const countiesWithDataInState = stateCounties.filter(c => {
              const countyData = status?.counties?.[c.id];
              return countyData?.parcels_in_database > 0;
            }).length;
            const apiCounties = stateCounties.filter(c => c.has_api).length;
            
            return (
              <div key={state} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div
                  onClick={() => toggleStateExpansion(state)}
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  data-testid={`state-${state}-btn`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && toggleStateExpansion(state)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">{state}</h3>
                      <p className="text-sm text-gray-500">
                        {stateCounties.length} counties | {stateParcels.toLocaleString()} parcels imported
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-blue-100 text-blue-700">
                      {countiesWithDataInState}/{stateCounties.length} imported
                    </Badge>
                    {apiCounties > 0 && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFetchState(state);
                        }}
                        disabled={fetchingState[state]}
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid={`fetch-state-${state}-btn`}
                      >
                        {fetchingState[state] ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fetching...</>
                        ) : (
                          <><Zap className="w-3 h-3 mr-1" /> Fetch All</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {stateCounties.map(countyInfo => {
                        const county = status?.counties?.[countyInfo.id] || {};
                        const isFetching = fetching[countyInfo.id] || false;
                        const hasData = county.parcels_in_database > 0;
                        
                        return (
                          <div key={countyInfo.id} className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h4 className="font-medium text-gray-900 text-sm">{countyInfo.city || county.name}</h4>
                                <p className="text-xs text-gray-500">
                                  {county.parcels_in_database?.toLocaleString() || 0} parcels
                                </p>
                              </div>
                              {hasData && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Imported</Badge>
                              )}
                            </div>
                            
                            <div className="flex gap-2 mt-3">
                              {countyInfo.has_api ? (
                                <Button
                                  onClick={() => handleAutoFetch(countyInfo.id)}
                                  disabled={isFetching}
                                  size="sm"
                                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                >
                                  {isFetching ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <><Play className="w-3 h-3 mr-1" /> {hasData ? 'Refresh' : 'Fetch'}</>
                                  )}
                                </Button>
                              ) : (
                                <a href={county.download_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                                  <Button size="sm" variant="outline" className="w-full text-xs">
                                    <Download className="w-3 h-3 mr-1" /> Manual
                                  </Button>
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Manual Import Tab */}
      {activeTab === 'manual' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Manual GIS File Import
          </h2>
          
          {/* Supported Formats Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Supported File Formats:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <FileJson className="w-4 h-4 text-green-600" />
                <span>.json, .geojson</span>
              </div>
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-blue-600" />
                <span>.zip (Shapefile)</span>
              </div>
              <div className="flex items-center gap-2">
                <Map className="w-4 h-4 text-red-600" />
                <span>.kml, .kmz</span>
              </div>
              <div className="flex items-center gap-2">
                <FileJson className="w-4 h-4 text-amber-600" />
                <span>.csv (with coords)</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* County Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select County
              </label>
              <select
                value={selectedCounty || ''}
                onChange={(e) => setSelectedCounty(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
                data-testid="county-select"
              >
                <option value="">-- Select a County --</option>
                {getStates().map(state => (
                  <optgroup key={state} label={state}>
                    {(status?.states?.[state] || []).map(countyInfo => (
                      <option key={countyInfo.id} value={countyInfo.id}>
                        {countyInfo.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>

              {selectedCounty && status?.counties?.[selectedCounty] && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Selected County Info</h4>
                  <p className="text-sm text-gray-600">
                    <strong>Name:</strong> {status.counties[selectedCounty].name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Current Parcels:</strong> {status.counties[selectedCounty].parcels_in_database?.toLocaleString() || 0}
                  </p>
                  {status.counties[selectedCounty].download_url && (
                    <a 
                      href={status.counties[selectedCounty].download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-2"
                    >
                      Data Source <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload GIS File
              </label>
              
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isUploading ? 'border-blue-400 bg-blue-50' :
                importFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-gray-400'
              }`}>
                <input
                  type="file"
                  accept=".json,.geojson,.zip,.kml,.kmz,.csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="gis-upload"
                  data-testid="gis-upload-input"
                  disabled={isUploading}
                />
                <label htmlFor="gis-upload" className={isUploading ? 'cursor-wait' : 'cursor-pointer'}>
                  {isUploading ? (
                    <div>
                      <Loader2 className="w-10 h-10 text-blue-600 mx-auto mb-2 animate-spin" />
                      <p className="font-medium text-blue-700">Uploading...</p>
                      <div className="w-full max-w-xs mx-auto mt-3">
                        <div className="flex justify-between text-xs text-blue-600 mb-1">
                          <span>Progress</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="w-full h-3 bg-blue-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-600 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-blue-500 mt-2">
                        {importFile?.size > 10 * 1024 * 1024 ? 'Uploading in chunks...' : 'Please wait...'}
                      </p>
                    </div>
                  ) : importFile ? (
                    <div>
                      <FileJson className="w-10 h-10 text-green-600 mx-auto mb-2" />
                      <p className="font-medium text-green-700">{importFile.name}</p>
                      <p className="text-sm text-green-600">
                        {(importFile.size / 1024 / 1024).toFixed(2)} MB
                        {importFile.size > 10 * 1024 * 1024 && (
                          <span className="text-amber-600 ml-2">(chunked upload)</span>
                        )}
                      </p>
                      <p className="text-xs text-green-500 mt-1">
                        {importFile.name.endsWith('.zip') ? 'Shapefile (ZIP)' : 
                         importFile.name.endsWith('.kml') || importFile.name.endsWith('.kmz') ? 'KML/KMZ' :
                         importFile.name.endsWith('.csv') ? 'CSV' : 'GeoJSON'}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">Click to upload GIS file</p>
                      <p className="text-sm text-gray-500">.json, .geojson, .zip, .kml, .kmz, .csv</p>
                      <p className="text-xs text-gray-400 mt-1">Large files (10MB+) uploaded in chunks</p>
                    </div>
                  )}
                </label>
              </div>

              <button
                onClick={handleImport}
                disabled={!selectedCounty || !importFile || isUploading}
                className={`mt-4 w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                  selectedCounty && importFile && !isUploading
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="import-btn"
              >
                {isUploading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Uploading ({uploadProgress}%)</>
                ) : (
                  <><Upload className="w-5 h-5" /> Import Parcel Data</>
                )}
              </button>
              
              {/* URL Download Option - for large files */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Alternative: Download from URL
                </h4>
                <p className="text-xs text-gray-500 mb-3">
                  For large files that fail to upload, paste a direct download link and the server will fetch it.
                </p>
                <div className="flex gap-2">
                  <input
                    type="url"
                    placeholder="https://example.com/parcels.zip"
                    className="flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    id="url-download-input"
                    data-testid="url-download-input"
                    disabled={!selectedCounty || isUploading}
                  />
                  <button
                    onClick={async () => {
                      const urlInput = document.getElementById('url-download-input');
                      const url = urlInput?.value?.trim();
                      if (!url || !selectedCounty) return;
                      
                      // Get token from localStorage
                      const token = localStorage.getItem('token');
                      if (!token) {
                        setMessage({ type: 'error', text: 'Not authenticated. Please log in again.' });
                        return;
                      }
                      
                      try {
                        setIsUploading(true);
                        setMessage({ type: 'info', text: 'Starting server-side download...' });
                        
                        const response = await fetch(`${API}/api/admin/usa-parcel-data/download-from-url`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            url: url,
                            county_id: selectedCounty
                          })
                        });
                        
                        const data = await response.json();
                        
                        if (response.ok) {
                          // If processing in background, poll for status
                          if (data.processing && data.process_id) {
                            setMessage({ type: 'info', text: data.message || 'Download started...' });
                            setUploadProgress(0);
                            urlInput.value = '';
                            
                            // Poll for status updates
                            const pollStatus = async () => {
                              try {
                                const statusResponse = await fetch(`${API}/api/admin/usa-parcel-data/import-status/${data.process_id}`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                
                                if (statusResponse.ok) {
                                  const statusData = await statusResponse.json();
                                  setUploadProgress(statusData.progress || 0);
                                  
                                  if (statusData.status === 'complete') {
                                    setMessage({ 
                                      type: 'success', 
                                      text: statusData.message || `Successfully imported ${(statusData.imported_count || 0).toLocaleString()} parcels`
                                    });
                                    setIsUploading(false);
                                    await fetchStatus();
                                    return; // Stop polling
                                  } else if (statusData.status === 'error') {
                                    setMessage({ type: 'error', text: statusData.message || 'Import failed' });
                                    setIsUploading(false);
                                    return; // Stop polling
                                  } else {
                                    // Still processing - update message and continue polling
                                    setMessage({ type: 'info', text: statusData.message || 'Processing...' });
                                    setTimeout(pollStatus, 3000); // Poll every 3 seconds
                                  }
                                } else {
                                  setTimeout(pollStatus, 5000);
                                }
                              } catch (e) {
                                console.error('Error polling status:', e);
                                setTimeout(pollStatus, 5000);
                              }
                            };
                            
                            // Start polling
                            setTimeout(pollStatus, 2000);
                            return; // Don't setIsUploading(false) yet
                          } else {
                            setMessage({ type: 'success', text: data.message || 'Download started!' });
                            urlInput.value = '';
                            setIsUploading(false);
                          }
                        } else {
                          setMessage({ type: 'error', text: data.detail || 'Failed to start download' });
                          setIsUploading(false);
                        }
                      } catch (error) {
                        setMessage({ type: 'error', text: `Error: ${error.message}` });
                        setIsUploading(false);
                      }
                    }}
                    disabled={!selectedCounty || isUploading}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      selectedCounty && !isUploading
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                    data-testid="url-download-btn"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Works with direct links to .zip, .geojson, .kml files. The download runs in the background.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <h3 className="font-semibold text-blue-900 flex items-center gap-2">
          <Info className="w-5 h-5" />
          How USA Parcel Data Works
        </h3>
        <ul className="mt-3 space-y-2 text-sm text-blue-800 list-disc list-inside">
          <li><strong>Auto-Fetch</strong> connects directly to each county's public ArcGIS REST API</li>
          <li>Downloads residential parcels with addresses and GPS coordinates</li>
          <li>Data is cached locally for instant polygon queries during job creation</li>
          <li>Fetching ~50,000 parcels per county takes about 5-10 minutes each</li>
          <li><strong>Manual Import</strong> supports GeoJSON, Shapefile (ZIP), KML/KMZ, and CSV with coordinates</li>
          <li>More counties can be added - contact support if you need a specific city</li>
        </ul>
      </div>

      {/* Diagnose & Normalize Modal */}
      {showNormalizeModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !normalizing && setShowNormalizeModal(false)}
          data-testid="normalize-modal-overlay"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="normalize-modal"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-rose-50 to-amber-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Diagnose &amp; Normalize Legacy County IDs</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  Maps legacy <code>county_id</code> values (e.g. <code>&quot;harris&quot;</code>) to canonical config keys (e.g. <code>&quot;harris_tx&quot;</code>).
                </p>
              </div>
              <button
                onClick={() => !normalizing && setShowNormalizeModal(false)}
                disabled={normalizing}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                data-testid="close-normalize-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto px-6 py-4">
              {loadingDiagnostic && !unmappedDiagnostic ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-rose-500" />
                </div>
              ) : unmappedDiagnostic ? (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <p className="text-xs text-emerald-700">Matched parcels</p>
                      <p className="text-xl font-bold text-emerald-900" data-testid="matched-parcel-count">
                        {(unmappedDiagnostic.total_matched_parcels || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                      <p className="text-xs text-rose-700">Unmatched parcels</p>
                      <p className="text-xl font-bold text-rose-900" data-testid="unmatched-parcel-count">
                        {(unmappedDiagnostic.total_unmatched_parcels || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-700">Unique county_id values</p>
                      <p className="text-xl font-bold text-blue-900">
                        {(unmappedDiagnostic.total_unique_county_ids || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm" data-testid="county-id-diagnostic-table">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Raw county_id in DB</th>
                          <th className="px-3 py-2 text-right font-semibold">Parcel count</th>
                          <th className="px-3 py-2 text-left font-semibold">Will be renamed to</th>
                          <th className="px-3 py-2 text-center font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(unmappedDiagnostic.rows || []).map((row, i) => (
                          <tr key={i} className={row.is_canonical ? '' : row.is_matched ? 'bg-amber-50/40' : 'bg-rose-50/40'}>
                            <td className="px-3 py-2 font-mono text-xs">{row.raw_county_id ?? <span className="text-gray-400">(null)</span>}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{(row.count || 0).toLocaleString()}</td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {row.is_canonical ? <span className="text-gray-400">— already canonical —</span> :
                               row.canonical_county_id ? <span className="text-emerald-700">{row.canonical_county_id}</span> :
                               <span className="text-rose-700">(no match — manual review)</span>}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {row.is_canonical ? (
                                <span className="text-emerald-700 text-xs font-semibold">OK</span>
                              ) : row.is_matched ? (
                                <span className="text-amber-700 text-xs font-semibold">Will rename</span>
                              ) : (
                                <span className="text-rose-700 text-xs font-semibold">Unmapped</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {Object.keys(unmappedDiagnostic.proposed_normalizations || {}).length > 0 && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                      <p className="font-semibold mb-1">Planned renames:</p>
                      <ul className="space-y-1">
                        {Object.entries(unmappedDiagnostic.proposed_normalizations).map(([canonical, info]) => (
                          <li key={canonical}>
                            <code className="text-amber-700">{canonical}</code> ← {(info.from || []).map(f => `"${f.raw}" (${f.count.toLocaleString()})`).join(', ')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">No diagnostic data loaded.</p>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-600">
                {unmappedDiagnostic && Object.keys(unmappedDiagnostic.proposed_normalizations || {}).length === 0
                  ? 'Nothing to normalize — all county_ids are already canonical.'
                  : 'Click "Normalize Now" to update MongoDB. This action cannot be undone automatically.'}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowNormalizeModal(false)}
                  disabled={normalizing}
                  className="text-sm"
                  data-testid="cancel-normalize-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={handleNormalizeCountyIds}
                  disabled={normalizing || !unmappedDiagnostic || Object.keys(unmappedDiagnostic?.proposed_normalizations || {}).length === 0}
                  className="bg-rose-500 hover:bg-rose-600 text-white text-sm"
                  data-testid="normalize-now-btn"
                >
                  {normalizing ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Normalizing...</> : 'Normalize Now'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Geo Data Repair Modal */}
      {showGeoRepairModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !repairingGeo && setShowGeoRepairModal(false)}
          data-testid="geo-repair-modal-overlay"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="geo-repair-modal"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Parcel Geo Data Health</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  Ensures parcels have the <code>location</code> GeoJSON field and a 2dsphere index so the &quot;Count Houses&quot; geo query works.
                </p>
              </div>
              <button
                onClick={() => !repairingGeo && setShowGeoRepairModal(false)}
                disabled={repairingGeo}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                data-testid="close-geo-repair-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto px-6 py-4">
              {/* Live progress bar shown during background repair */}
              {geoRepairProgress && (
                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-orange-900" data-testid="geo-repair-progress-message">
                      {geoRepairProgress.message || 'Repair in progress...'}
                    </p>
                    <span className="text-sm font-bold text-orange-900 tabular-nums">
                      {Math.min(geoRepairProgress.progress || 0, 100)}%
                    </span>
                  </div>
                  <div className="w-full h-3 bg-orange-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-500 transition-all duration-300 ease-out"
                      style={{ width: `${Math.min(geoRepairProgress.progress || 0, 100)}%` }}
                      data-testid="geo-repair-progress-bar"
                    />
                  </div>
                  <p className="text-xs text-orange-700 mt-2">
                    Backfilled {(geoRepairProgress.backfilled || 0).toLocaleString()} / {(geoRepairProgress.total_to_backfill || 0).toLocaleString()} parcels
                    {geoRepairProgress.status === 'running' && ' • This runs in the background — safe to keep this tab open or come back later.'}
                  </p>
                </div>
              )}

              {loadingGeoDiagnostic && !geoDiagnostic ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
                </div>
              ) : geoDiagnostic ? (
                <>
                  {geoDiagnostic.counts_accurate === false && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                      <strong>Note:</strong> The location-field counts below are <em>estimated</em> from a 5,000-doc sample (your collection is too large for an exact count to finish within the request timeout). The repair itself still runs accurately over every document.
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <p className="text-xs text-slate-700">Total parcels</p>
                      <p className="text-xl font-bold text-slate-900">{(geoDiagnostic.total_parcels || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                      <p className="text-xs text-emerald-700">With <code>location</code> field {geoDiagnostic.counts_accurate === false ? '(est.)' : ''}</p>
                      <p className="text-xl font-bold text-emerald-900" data-testid="geo-with-location-count">
                        {(geoDiagnostic.parcels_with_location_field || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className={`${geoDiagnostic.parcels_missing_location_field > 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'} border rounded-lg p-3`}>
                      <p className={`text-xs ${geoDiagnostic.parcels_missing_location_field > 0 ? 'text-rose-700' : 'text-slate-700'}`}>Missing <code>location</code> field {geoDiagnostic.counts_accurate === false ? '(est.)' : ''}</p>
                      <p className={`text-xl font-bold ${geoDiagnostic.parcels_missing_location_field > 0 ? 'text-rose-900' : 'text-slate-900'}`} data-testid="geo-missing-location-count">
                        {(geoDiagnostic.parcels_missing_location_field || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className={`${geoDiagnostic.has_2dsphere_index_on_location ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'} border rounded-lg p-3`}>
                      <p className={`text-xs ${geoDiagnostic.has_2dsphere_index_on_location ? 'text-emerald-700' : 'text-rose-700'}`}>2dsphere index</p>
                      <p className={`text-xl font-bold ${geoDiagnostic.has_2dsphere_index_on_location ? 'text-emerald-900' : 'text-rose-900'}`} data-testid="geo-index-status">
                        {geoDiagnostic.has_2dsphere_index_on_location ? 'Present' : 'Missing'}
                      </p>
                    </div>
                  </div>

                  {geoDiagnostic.sample && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-1">
                      <p className="font-semibold text-gray-700">Sample parcel:</p>
                      <p>• has_latitude: <code>{String(geoDiagnostic.sample.has_latitude)}</code></p>
                      <p>• has_longitude: <code>{String(geoDiagnostic.sample.has_longitude)}</code></p>
                      <p>• has_location_field: <code>{String(geoDiagnostic.sample.has_location_field)}</code></p>
                      {geoDiagnostic.sample.location_format && (
                        <p>• location: <code className="break-all">{geoDiagnostic.sample.location_format}</code></p>
                      )}
                      <p>• county_id_example: <code>{geoDiagnostic.sample.county_id_example ?? '(null)'}</code></p>
                    </div>
                  )}

                  <div className="mt-4 text-sm text-gray-700">
                    {geoDiagnostic.needs_repair ? (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <p className="font-semibold text-orange-900">Repair needed.</p>
                        <p className="text-xs text-orange-700 mt-1">
                          {geoDiagnostic.parcels_missing_location_field > 0
                            ? `${geoDiagnostic.parcels_missing_location_field.toLocaleString()} parcels are missing the location field — they will be backfilled from latitude/longitude.`
                            : 'The 2dsphere index is missing and will be created.'}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <p className="font-semibold text-emerald-900">All clear — geo data is healthy.</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">No diagnostic data loaded.</p>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-600">
                Large datasets (millions of rows) may take several minutes. Safe to re-run.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowGeoRepairModal(false)}
                  disabled={repairingGeo}
                  className="text-sm"
                  data-testid="cancel-geo-repair-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={handleRepairGeoData}
                  disabled={repairingGeo || !geoDiagnostic || !geoDiagnostic.needs_repair}
                  className="bg-orange-500 hover:bg-orange-600 text-white text-sm"
                  data-testid="repair-geo-now-btn"
                >
                  {repairingGeo ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Repairing...</> : 'Repair Geo Data Now'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test-on-Map Modal — preview a county's parcel coverage on a Google Map */}
      {testMapCounty && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => { setTestMapCounty(null); setTestMapBounds(null); }}
          data-testid="test-map-modal-overlay"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="test-map-modal"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Map className="w-5 h-5 text-blue-600" />
                  {testMapCounty.county_name} — Parcel Coverage
                </h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {testMapBounds
                    ? `${testMapBounds.parcel_count.toLocaleString()} parcels — bounding box shown in blue`
                    : 'Loading bounds…'}
                </p>
              </div>
              <button
                onClick={() => { setTestMapCounty(null); setTestMapBounds(null); }}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-test-map-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 min-h-[500px] relative">
              {loadingTestMap || !testMapBounds ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <LazyGoogleMap
                  center={{ lat: testMapBounds.center.lat, lng: testMapBounds.center.lng }}
                  zoom={10}
                  style={{ height: '500px', width: '100%' }}
                  polygon={[
                    [testMapBounds.bounds.min_lat, testMapBounds.bounds.min_lng],
                    [testMapBounds.bounds.min_lat, testMapBounds.bounds.max_lng],
                    [testMapBounds.bounds.max_lat, testMapBounds.bounds.max_lng],
                    [testMapBounds.bounds.max_lat, testMapBounds.bounds.min_lng],
                  ]}
                  polygonOptions={{
                    fillColor: '#3b82f6',
                    fillOpacity: 0.12,
                    strokeColor: '#3b82f6',
                    strokeWeight: 2,
                  }}
                />
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-600">
                Use the Create Job page to draw a real delivery zone inside these bounds and click &quot;Count Houses&quot;.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setTestMapCounty(null); setTestMapBounds(null); }}
                  className="text-sm"
                  data-testid="close-test-map-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    if (testMapBounds) {
                      sessionStorage.setItem('parcel_zoom_to', JSON.stringify({
                        center: testMapBounds.center,
                        county_name: testMapCounty.county_name,
                      }));
                      navigate('/admin/create-job');
                    }
                  }}
                  disabled={!testMapBounds}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                  data-testid="jump-to-create-job-btn"
                >
                  <MapPin className="w-4 h-4 mr-1" />
                  Open in Create Job
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminParcelData;
